import type { BrandListItem } from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";

import { memo, useCallback, useMemo, useState } from "react";
import { Button } from "react-aria-components";

import { DraggableTable as Table } from "@/components/table";
import { useDebounce } from "@/hooks/useDebounce";
import { dateFormatterWithConditionalYear } from "@/utils/dateFormatterWithConditionalYear";

import { useGetTrashedBrands } from "../hooks/useGetTrashedBrands";
import { useRestoreBrands } from "../mutations/useRestoreBrands";

interface TrashedBrandsTableProps {
  search?: string;
}

const NameCell = memo(function NameCell({
  row,
}: {
  row: { original: BrandListItem };
}) {
  return (
    <div className="font-medium text-text-primary text-sm leading-tight truncate">
      {row.original.name}
    </div>
  );
});

const AccountCell = memo(function AccountCell({
  row,
}: {
  row: { original: BrandListItem };
}) {
  return (
    <div className="font-medium text-text-primary text-sm leading-tight truncate">
      {row.original.account?.name || "-"}
    </div>
  );
});

const DeletedAtCell = memo(function DeletedAtCell({
  row,
}: {
  row: { original: BrandListItem };
}) {
  return (
    <span className="text-text-tertiary text-sm truncate">
      {row.original.deletedAt
        ? dateFormatterWithConditionalYear(row.original.deletedAt)
        : "-"}
    </span>
  );
});

const DeletedByCell = memo(function DeletedByCell({
  row,
}: {
  row: { original: BrandListItem };
}) {
  const deletedBy = row.original.deletedBy;

  if (!deletedBy) {
    return <span className="text-text-tertiary text-sm truncate">-</span>;
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      {deletedBy.profilePicture && (
        <img
          alt={deletedBy.name}
          className="size-6 rounded-full object-cover flex-shrink-0"
          src={deletedBy.profilePicture}
        />
      )}
      <span className="text-text-primary font-medium text-sm truncate">
        {deletedBy.name}
      </span>
    </div>
  );
});

const ActionCell = memo(function ActionCell({
  isPending,
  onRestore,
  row,
}: {
  isPending: boolean;
  onRestore: (id: string) => void;
  row: { original: BrandListItem };
}) {
  return (
    <Button
      className="px-4 py-2 text-sm font-medium text-text-brand-primary bg-bg-primary border border-border-brand rounded-md hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
      isDisabled={isPending}
      onPress={() => onRestore(row.original.id)}
    >
      Restore
    </Button>
  );
});

export const TrashedBrandsTable = memo(function TrashedBrandsTable({
  search,
}: TrashedBrandsTableProps) {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const debouncedQuery = useDebounce(search, 500, () => setPage(1));

  const { data, isLoading } = useGetTrashedBrands({
    itemsPerPage,
    page,
    search: debouncedQuery,
  });

  const restoreMutation = useRestoreBrands();

  const handleRestore = useCallback(
    (id: string) => {
      restoreMutation.mutate(Number(id));
    },
    [restoreMutation],
  );

  const columns: ColumnDef<BrandListItem>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        cell: ({ row }) => <NameCell row={row} />,
        enableSorting: false,
        header: "Name",
        size: 200,
      },
      {
        accessorKey: "account",
        cell: ({ row }) => <AccountCell row={row} />,
        enableSorting: false,
        header: "Account",
        size: 200,
      },
      {
        accessorKey: "deletedAt",
        cell: ({ row }) => <DeletedAtCell row={row} />,
        enableSorting: false,
        header: "Deleted on",
        size: 150,
      },
      {
        accessorKey: "deletedBy",
        cell: ({ row }) => <DeletedByCell row={row} />,
        enableSorting: false,
        header: "Deleted by",
        size: 150,
      },
      {
        accessorKey: "id",
        cell: ({ row }) => (
          <ActionCell
            isPending={restoreMutation.isPending}
            onRestore={handleRestore}
            row={row}
          />
        ),
        enableSorting: false,
        header: "Action",
        size: 120,
      },
    ],
    [handleRestore, restoreMutation.isPending],
  );

  const paginationConfig = useMemo(() => {
    if (!data?.metadata) return;

    return {
      metadata: {
        currentPage: data.metadata.currentPage,
        itemsPerPage: data.metadata.itemsPerPage,
        totalItems: data.metadata.totalItems,
        totalPages: data.metadata.totalPages,
      },
      onItemsPerPageChange: (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setPage(1);
      },
      onPageChange: (newPage: number) => setPage(newPage),
      pageSizeOptions: [10, 25, 50, 100],
      showPageSizeSelector: true,
    };
  }, [data]);

  const getRowId = useCallback((brand: BrandListItem) => brand.id, []);

  if (!isLoading && (!data?.brands || data.brands.length === 0)) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <p className="text-text-secondary">No deleted brands found</p>
      </div>
    );
  }

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto trashed-table">
      <style>{`
        .trashed-table tbody tr {
          height: 60px !important;
        }
        .trashed-table tbody td {
          max-width: 0;
          overflow: hidden;
        }
        .trashed-table tbody td > div,
        .trashed-table tbody td > span,
        .trashed-table tbody td > a {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }
      `}</style>
      <Table
        className="rounded-b-xl w-full"
        columns={columns}
        data={data?.brands || []}
        getRowId={getRowId}
        isLoading={isLoading}
        pagination={paginationConfig}
      />
    </div>
  );
});
